import React, { useEffect, useState } from "react";
import Footer from "../../CoreComponents/Footer";
import Header from "../../CoreComponents/Header";
import MainHeader from "../../CoreComponents/MainHeader";
import NavBar from "../../CoreComponents/NavBar";
import { showMessage } from "../../Functionalities/Functions";
import { canManagePayouts } from "../Payouts/Payouts";
import { todayInIndia } from "../Payouts/PayoutFormModal";
import { count, fmtDate } from "../Insights/format";
import { formatSeconds, loadResponsiveness, rateTone } from "./Logic";

const TONE_STYLES = {
  good: { background: "#ecfdf5", color: "#065f46", border: "1px solid #a7f3d0" },
  warn: { background: "#fffbeb", color: "#92400e", border: "1px solid #fde68a" },
  bad: { background: "#fff1f2", color: "#be123c", border: "1px solid #fecdd3" },
};

const daysBefore = (isoDay, days) => {
  const [year, month, day] = isoDay.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day - days)).toISOString().slice(0, 10);
};

const formatTimestamp = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })
    : "—";

export default function RestaurantResponse() {
  const today = todayInIndia();
  const [pending, setPending] = useState({ from: daysBefore(today, 6), to: today });
  const [filters, setFilters] = useState(pending);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const allowed = canManagePayouts();

  useEffect(() => {
    if (!allowed) return;
    setLoading(true);
    loadResponsiveness(filters).then((body) => {
      setLoading(false);
      if (body && body.Status === true) {
        setReport(body);
        return;
      }
      setReport(null);
      showMessage((body && body.Error) || "Could not load restaurant response", "danger", "Error!", "top", "top-right");
    });
  }, [filters, allowed]);

  const apply = (event) => {
    event.preventDefault();
    setFilters(pending);
  };

  const rows = report ? report.restaurants : [];

  return (
    <div id="main-wrapper">
      <MainHeader />
      <Header name={"Restaurant response"} />
      <NavBar />

      <div className="content-body" style={{ minHeight: "100vh" }}>
        <div className="container-fluid">
          {!allowed ? (
            <div className="card">
              <div className="card-body">Only superusers and franchise admins can see restaurant response.</div>
            </div>
          ) : (
            <div className="card">
              <div className="card-header d-flex flex-wrap align-items-center justify-content-between">
                <div>
                  <h4 className="card-title mb-1">How quickly restaurants answer new orders</h4>
                  <p className="mb-0 text-muted">
                    From the moment an order is approved to the restaurant tapping Accept or Reject in its app. Slowest first.
                  </p>
                </div>
              </div>
              <div className="card-body">
                <form className="row align-items-end mb-3" onSubmit={apply}>
                  <div className="col-md-3 mb-2">
                    <label className="text-black font-w500">Orders from</label>
                    <input
                      className="form-control"
                      type="date"
                      max={pending.to}
                      value={pending.from}
                      onChange={(event) => setPending((prev) => ({ ...prev, from: event.target.value }))}
                    />
                  </div>
                  <div className="col-md-3 mb-2">
                    <label className="text-black font-w500">Orders to</label>
                    <input
                      className="form-control"
                      type="date"
                      min={pending.from}
                      max={today}
                      value={pending.to}
                      onChange={(event) => setPending((prev) => ({ ...prev, to: event.target.value }))}
                    />
                  </div>
                  <div className="col-md-2 mb-2">
                    <button type="submit" className="btn btn-outline-primary btn-block">
                      Apply
                    </button>
                  </div>
                </form>

                <div className="table-responsive">
                  <table className="table table-sm dataTablesCard">
                    <thead>
                      <tr>
                        <th>Restaurant</th>
                        <th className="text-center">Answer rate</th>
                        <th className="text-right">Received</th>
                        <th className="text-right">Answered</th>
                        <th className="text-right">Not answered</th>
                        <th className="text-right">Accepted</th>
                        <th className="text-right">Rejected</th>
                        <th className="text-right">Typical response</th>
                        <th className="text-right">Slowest</th>
                        <th className="text-center">Phones with alarm on</th>
                        <th>Last answer</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={11} className="text-center py-4">
                            Loading…
                          </td>
                        </tr>
                      ) : rows.length === 0 ? (
                        <tr>
                          <td colSpan={11} className="text-center py-4 text-muted">
                            No approved orders in these dates.
                          </td>
                        </tr>
                      ) : (
                        rows.map((row) => (
                          <tr key={row.branch_id}>
                            <td className="font-w600">{row.branch_name}</td>
                            <td className="text-center">
                              <span
                                style={{ ...TONE_STYLES[rateTone(row.answer_rate)], borderRadius: 999, padding: "2px 10px", fontWeight: 700 }}
                              >
                                {`${row.answer_rate}%`}
                              </span>
                            </td>
                            <td className="text-right">{count(row.received)}</td>
                            <td className="text-right">{count(row.answered)}</td>
                            <td className="text-right" style={{ color: row.unanswered ? "#be123c" : undefined, fontWeight: row.unanswered ? 700 : 400 }}>
                              {count(row.unanswered)}
                            </td>
                            <td className="text-right">{count(row.accepted)}</td>
                            <td className="text-right">{count(row.rejected)}</td>
                            <td className="text-right">{formatSeconds(row.median_response_seconds)}</td>
                            <td className="text-right">{formatSeconds(row.slowest_response_seconds)}</td>
                            <td className="text-center">{`${row.phones_alarm_on} of ${row.phones_registered}`}</td>
                            <td style={{ whiteSpace: "nowrap" }}>{formatTimestamp(row.last_answer_at)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {report && (
                  <p className="text-muted mb-0 mt-2">{`Orders placed ${fmtDate(report.range.from)} – ${fmtDate(report.range.to)}.`}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
